import { useState } from "react";
import type { RunRow } from "../lib/types";
import { useUnits } from "../hooks/useUnits";
import { fmtRate, fmtWeight, parseWeightInput, weightToInput } from "../lib/units";
import { RunFeedback } from "./dashboard/RunFeedback";

function fmtAgo(epochSeconds: number): string {
  const s = Date.now() / 1000 - epochSeconds;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function fmtDuration(ms: number): string {
  const s = Math.floor((ms || 0) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s % 60}s`;
}

export type RunEditFields = { end_grams: string; qa_score: string; qa_notes: string };

/**
 * One run row for the dashboard's Today / All runs tabs — ported from
 * legacy dashboard.html's renderRun(), minus photos (not in this
 * milestone's scope) and the merge-mode checkbox (merge UI wasn't
 * requested for M5b). Edit is inline (grams + QA only, "edit-lite" per the
 * brief) rather than legacy's full modal — PATCH /api/runs/:id reads
 * whatever form fields are present and leaves the rest alone.
 */
export function RunCard({
  run, onSave, onDelete,
}: {
  run: RunRow;
  onSave: (id: number, fields: RunEditFields) => Promise<void>;
  onDelete: (id: number) => void;
}) {
  const units = useUnits();
  const [editing, setEditing] = useState(false);
  const [endGrams, setEndGrams] = useState(String(run.end_grams ?? ""));
  const [qaScore, setQaScore] = useState(String(run.qa_score ?? ""));
  const [qaNotes, setQaNotes] = useState(run.qa_notes ?? "");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(false);

  async function save() {
    setSaving(true);
    const grams = units === "imperial" && endGrams.trim() !== "" ? String(parseWeightInput(endGrams, units)) : endGrams;
    await onSave(run.id, { end_grams: grams, qa_score: qaScore, qa_notes: qaNotes });
    setSaving(false);
    setEditing(false);
  }

  return (
    <div className="run-card">
      <div>
        <div className="name">
          {run.player_name} · {run.strain_name || "—"}
          {run.merged_from ? <span className="pill" style={{ marginLeft: 6 }}>merged</span> : null}
        </div>
        <div className="meta">
          {fmtAgo(run.created_at)} · {run.mode}
          {run.harvest_name ? ` · 🌾 ${run.harvest_name}` : ""}
          {run.superseded_by ? " · superseded" : ""}
          {run.tombstone ? " · deleted" : ""}
        </div>
        {(run.qa_score != null || run.yield_pct != null) && (
          <div className="meta">
            {run.qa_score != null ? `QA ${run.qa_score}/10` : ""}
            {run.yield_pct != null ? ` · Yield ${run.yield_pct.toFixed(1)}%` : ""}
            {run.qa_notes ? ` — ${run.qa_notes}` : ""}
          </div>
        )}
      </div>
      <div className="metrics">
        <div className="big">{run.end_grams != null ? fmtWeight(run.end_grams, units, 1) : "—"}</div>
        <div>{run.gpm ? fmtRate(run.gpm, units, 1) : ""}</div>
        <div>{fmtDuration(run.active_ms)}</div>
      </div>

      {editing ? (
        <div className="run-actions edit-run-form">
          <div className="form-2col">
            <input
              data-testid={`edit-grams-${run.id}`}
              type="number" step="0.1" min="0" value={endGrams}
              aria-label={units === "imperial" ? "End weight (oz)" : "End grams"}
              onChange={(e) => setEndGrams(e.target.value)}
              placeholder={units === "imperial" ? "End oz" : "End grams"}
            />
            <input
              data-testid={`edit-qa-${run.id}`}
              type="number" min="1" max="10" step="1" value={qaScore}
              aria-label="QA score"
              onChange={(e) => setQaScore(e.target.value)}
              placeholder="QA 1-10"
            />
          </div>
          <input
            value={qaNotes} aria-label="QA notes"
            onChange={(e) => setQaNotes(e.target.value)}
            placeholder="QA notes (optional)"
          />
          <div className="btn-row">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Cancel</button>
            <button
              type="button" className="btn btn-primary btn-sm" data-testid={`save-run-${run.id}`}
              disabled={saving} onClick={save}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="run-actions">
          <button type="button" className="btn btn-ghost btn-sm" data-testid={`edit-run-${run.id}`} onClick={() => { setEndGrams(run.end_grams != null ? weightToInput(run.end_grams, units) : ""); setEditing(true); }}>
            Edit
          </button>
          <button type="button" className="btn btn-ghost btn-sm" data-testid={`feedback-run-${run.id}`} onClick={() => setFeedback(!feedback)}>
            Feedback
          </button>
          <button type="button" className="btn btn-ghost btn-sm" data-testid={`delete-run-${run.id}`} onClick={() => onDelete(run.id)}>
            Delete
          </button>
        </div>
      )}
      {feedback && <RunFeedback runId={run.id} />}
    </div>
  );
}
